import $ from "jquery";
import Swiper from "swiper";
import { Navigation, Pagination, Scrollbar, Autoplay, Thumbs, EffectCoverflow, EffectFade } from "swiper/modules";
Swiper.use([Navigation, Pagination, Scrollbar, Autoplay, Thumbs, EffectCoverflow, EffectFade]);
window.Swiper = Swiper;

import "swiper/css/bundle";

// Define slider block configurations - single source of truth
const SLIDER_CONFIG = {
    ".ms_content-carousel": { main: ".swiper", thumb: null, selector: "swiper" },
    ".ms_carousel": { main: ".carousel", thumb: ".thumbnail-carousel", selector: "carousel" },
    ".ms_cards": [
        { main: ".card-slider", thumb: null, selector: "card-slider" },
        { main: ".card-mobile-slider", thumb: null, selector: "card-mobile-slider" },
    ],
    ".ms_products": [
        { main: ".product-slider", thumb: null, selector: "product-slider" },
        { main: ".product-mobile-slider", thumb: null, selector: "product-mobile-slider" },
    ],
    ".ms_testimonial-quote": { main: ".item-slider", thumb: null, selector: "item-slider" },
    ".ms_usps": { main: ".usps-slider", thumb: null, selector: "usps-slider" },
};

// Generate selectors from config
const SLIDER_SELECTORS = {
    containers: Object.keys(SLIDER_CONFIG).join(", "),
    swipers: Object.values(SLIDER_CONFIG)
        .flatMap((cfg) => (Array.isArray(cfg) ? cfg : [cfg]))
        .flatMap((cfg) => [cfg.main, cfg.thumb].filter(Boolean))
        .join(", "),
};

const initializeSliders = function (sliders) {
    // Helper to parse options from data attribute
    const parseOptions = (el) => (el ? JSON.parse(el.dataset.sliderOptions || "{}") : {});

    // Helper to calculate and apply container alignment
    const applyContainerAlignment = (options, mainEl, sliderId) => {
        const shouldAlign = options.alignWithContainer === true;

        if (!shouldAlign) {
            return;
        }

        const calculateOffset = () => {
            const containerEl = document.querySelector(".container");
            const sliderEl = document.querySelector(`#${sliderId} ${mainEl.classList.contains('swiper') ? '.swiper' : '.' + Array.from(mainEl.classList).find(c => c.includes('slider') || c.includes('carousel') || c === mainEl.className)}`);

            if (containerEl && sliderEl) {
                const containerRect = containerEl.getBoundingClientRect();
                const sliderRect = sliderEl.getBoundingClientRect();

                // Get container's left padding
                const containerPaddingLeft = parseFloat(window.getComputedStyle(containerEl).paddingLeft) || 0;

                const containerOffset = containerRect.left + window.scrollX + containerPaddingLeft;
                const sliderOffset = sliderRect.left + window.scrollX;
                const offsetValue = Math.max(0, containerOffset - sliderOffset);

                if (offsetValue > 0) {
                    options.slidesOffsetBefore = offsetValue;
                    options.slidesOffsetAfter = offsetValue;
                }
            }
        };

        // Calculate initial offset
        calculateOffset();

        // Recalculate on resize
        window.addEventListener('resize', calculateOffset);
    };

    // Helper to assign unique navigation button classes and update options
    const assignNavButtons = (sliderId, options, type = "main") => {
        ["prev", "next"].forEach((btn) => {
            const selector =
                type === "main"
                    ? `#${sliderId} .swiper-button-${btn}.main-button, #${sliderId} .swiper-button-${btn}`
                    : `#${sliderId} .swiper-button-${btn}.thumbnail-button`;
            const button = document.querySelector(selector);
            if (button) {
                const btnClass = `${sliderId}-${type}-${btn}`;
                button.classList.add(btnClass);
                options.navigation = options.navigation || {};
                options.navigation[`${btn}El`] = `.${btnClass}`;
            }
        });
    };

    // Helper to handle reduced motion settings
    const applyReducedMotion = (options, reduced) => {
        if (reduced) {
            options.autoplay = false;
            options.speed = 0;
        }
    };

    // Helper to (re)initialize Swiper instances
    const reinitSlider = (selector, options, onSlideChange) => {
        const instance = new Swiper(selector, options);
        if (onSlideChange) instance.on("slideChange", onSlideChange);
        return instance;
    };

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    if (sliders.thumb != null) {
        let mainOptions = parseOptions(sliders.main);
        let thumbOptions = parseOptions(sliders.thumb);

        assignNavButtons(sliders.id, mainOptions, "main");
        assignNavButtons(sliders.id, thumbOptions, "thumb");
        applyContainerAlignment(mainOptions, sliders.main, sliders.id);

        const originalMainOptions = JSON.parse(JSON.stringify(mainOptions));
        const originalThumbOptions = JSON.parse(JSON.stringify(thumbOptions));

        applyReducedMotion(mainOptions, !mediaQuery || mediaQuery.matches);
        applyReducedMotion(thumbOptions, !mediaQuery || mediaQuery.matches);

        let thumbSlider = reinitSlider(`#${sliders.id} .thumbnail-carousel`, thumbOptions);
        mainOptions.thumbs = { swiper: thumbSlider };
        let mainSlider = reinitSlider(`#${sliders.id} .carousel`, mainOptions, slideChange);

        function slideChange() {
            const currentSlide = mainSlider.slides[mainSlider.activeIndex];
            if (!currentSlide) return;
            if (currentSlide.classList.contains("youtube")) {
                const iframe = currentSlide.querySelector("iframe");
                // Uncomment to play video:
                // if (iframe) iframe.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
            } else {
                mainSlider.slides.forEach((slide) => {
                    const iframe = slide.querySelector("iframe");
                    if (iframe) {
                        iframe.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', "*");
                    }
                });
            }
        }

        mediaQuery.addEventListener("change", () => {
            mainSlider?.destroy();
            thumbSlider?.destroy();

            mainOptions = JSON.parse(JSON.stringify(originalMainOptions));
            thumbOptions = JSON.parse(JSON.stringify(originalThumbOptions));
            applyReducedMotion(mainOptions, mediaQuery.matches);
            applyReducedMotion(thumbOptions, mediaQuery.matches);

            thumbSlider = reinitSlider(`#${sliders.id} .thumbnail-carousel`, thumbOptions);
            mainOptions.thumbs = { swiper: thumbSlider };
            mainSlider = reinitSlider(`#${sliders.id} .carousel`, mainOptions, slideChange);
        });
    } else {
        let mainOptions = parseOptions(sliders.main);
        assignNavButtons(sliders.id, mainOptions, "main");
        applyContainerAlignment(mainOptions, sliders.main, sliders.id);

        const originalMainOptions = JSON.parse(JSON.stringify(mainOptions));
        applyReducedMotion(mainOptions, !mediaQuery || mediaQuery.matches);

        const swiperSelector = sliders?.selector ? `#${sliders.id} .${sliders.selector}` : `#${sliders.id} .carousel`;
        let mainSlider = reinitSlider(swiperSelector, mainOptions);

        mediaQuery.addEventListener("change", () => {
            mainSlider?.destroy();
            mainOptions = JSON.parse(JSON.stringify(originalMainOptions));
            applyReducedMotion(mainOptions, mediaQuery.matches);
            mainSlider = reinitSlider(swiperSelector, mainOptions);
        });

        if (typeof cardSliders !== "undefined") {
            cardSliders = cardSliders.map(({ container, id }) => {
                if (id === sliders.id) {
                    return { id, container, instance: mainSlider };
                }
                return { id, container };
            });
        }
    }
};
window.initializeSliders = initializeSliders;

// Apply above function to all sliders.
function msInitializeDynamicSliders() {
    let listOfSliders = [];

    // Loop through each slider block type from config
    Object.entries(SLIDER_CONFIG).forEach(([containerSelector, config]) => {
        document.querySelectorAll(containerSelector).forEach((container) => {
            // Handle both single config and array of configs
            const configs = Array.isArray(config) ? config : [config];

            configs.forEach((cfg) => {
                const mainElement = container.querySelector(cfg.main);
                if (mainElement) {
                    listOfSliders.push({
                        id: container.id,
                        main: mainElement,
                        thumb: cfg.thumb ? container.querySelector(cfg.thumb) : null,
                        selector: cfg.selector,
                    });
                }
            });
        });
    });

    if (listOfSliders.length !== 0) {
        listOfSliders.forEach((sliders) => {
            initializeSliders(sliders);
        });
    }
}

document.addEventListener(
    `DOMContentLoaded`,
    function () {
        msInitializeDynamicSliders();
    },
    { passive: true },
);

// For WordPress block editor iframe support
if (typeof wp !== "undefined" && wp.hooks && typeof acf === "undefined") {
    // Use MutationObserver to detect when new slider elements are added to the iframe
    if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", setupMutationObserver);
    } else {
        setupMutationObserver();
    }

    function setupMutationObserver() {
        let mutationTimeout;
        let isReinitializing = false;

        const observer = new MutationObserver(() => {
            // Skip if we're already reinitializing
            if (isReinitializing) {
                return;
            }

            // Debounce to prevent infinite loop from DOM modifications during initialization
            clearTimeout(mutationTimeout);
            mutationTimeout = setTimeout(() => {
                // Check if any swiper element doesn't have the swiper-initialized class
                const swiperSelector = SLIDER_SELECTORS.swipers
                    .split(", ")
                    .map((sel) => `${sel}:not(.swiper-initialized)`)
                    .join(", ");
                const uninitializedSwiper = document.querySelector(swiperSelector);

                if (uninitializedSwiper) {
                    isReinitializing = true;
                    msInitializeDynamicSliders();
                    // Reset flag after initialization completes
                    setTimeout(() => {
                        isReinitializing = false;
                    }, 500);
                }
            }, 300); // Wait 300ms after mutations stop before reinitializing
        });

        observer.observe(document.body || document.documentElement, {
            childList: true,
            subtree: true,
            attributes: false,
            characterData: false,
        });
    }
}

if (typeof acf !== "undefined") {
    // ACF hook for non-iframed editors
    acf.addAction(`render_block_preview`, function ($el, attributes) {
        msInitializeDynamicSliders();
    });
}

$(".swiper-button-next, .swiper-button-prev").on("mousedown", function (e) {
    e.preventDefault();
});
